import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import { useSiteScope } from '../../core/site-scope';
import { submitQueueable } from '../../offline-queue';
import type {
  ImpactUrgency,
  SiteIncidentStatus,
  SiteIncidentCreate,
  SiteIncidentDetail,
  SiteIncidentDraftPatch,
  SiteIncidentRecord,
  SiteIncidentResolutionCode,
  SiteIncidentHoldReason,
  KnowledgeEntry,
  KnowledgeQuery,
  KbUsageAction,
  KbDraftCreate,
  KbDraftResult,
  ScheduleRecord,
} from './site-itsm.types';

const BASE = '/v1/itsm/incidents';
const KEY = ['site-itsm'] as const;

/** Assignment-picker directory from the site users replica (R-133).
 *
 * ⚠ `maintenance: read`, which is exactly the reach it needs — but it means this
 * is NOT a general "who works here" list. Procurement asked it for its people and
 * got a 403 for the Requester seat; that field now has its own `procurement: read`
 * endpoint. Any screen outside maintenance needs the same treatment. */
export interface DirectoryEntry {
  id: string;
  name: string;
  email: string;
}
export function useSiteDirectory() {
  // `scope` + `ready` are not optional here: without them this fires during the
  // cloud-mode no-site window, `resolvePath` throws NO_SITE_SELECTED, and the
  // error caches under a key that never changes when the site arrives — so the
  // picker stays empty for ever. See core/site-scope.ts; it is the same fault
  // that once hid `+ New request`.
  const { scope, ready } = useSiteScope();
  return useQuery({
    enabled: ready,
    queryKey: [...KEY, scope, 'directory'],
    queryFn: () => api.get<DirectoryEntry[]>(`${BASE}/directory`),
    staleTime: 300_000,
  });
}

/** E-1: the site-tier priority matrix (impact × urgency → P1..P4), served from
 *  the commons replica. Read once + cached; the create form previews the cell
 *  the server would compute (the server stays authoritative). */
export interface SitePriorityEntry {
  impact: ImpactUrgency;
  urgency: ImpactUrgency;
  priority: 'P1' | 'P2' | 'P3' | 'P4';
}
export function useSitePriorityMatrix() {
  return useQuery({
    queryKey: [...KEY, 'priority-matrix'],
    queryFn: () => api.get<SitePriorityEntry[]>('/v1/itsm/priority-matrix'),
    staleTime: 5 * 60_000,
  });
}

export function useSiteIncidents(openOnly = false) {
  return useQuery({
    queryKey: [...KEY, { openOnly }],
    queryFn: () => api.get<SiteIncidentRecord[]>(`${BASE}?open_only=${openOnly}`),
    refetchInterval: 30_000,
  });
}

/** G-F3/L-7: the site's coverage/maintenance/freeze schedules + weekly spans. */
export function useSchedules() {
  return useQuery({
    queryKey: [...KEY, 'schedules'],
    queryFn: () => api.get<ScheduleRecord[]>('/v1/itsm/schedules'),
    staleTime: 5 * 60_000,
  });
}

export function useSiteIncident(id: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => api.get<SiteIncidentDetail>(`${BASE}/${id}`),
    enabled: !!id,
  });
}

export function useCreateSiteIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SiteIncidentCreate) => api.post<SiteIncidentRecord>(BASE, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** R-244 — "+ Report an incident" writes the DRAFT and you land on it.
 *
 * The prototype's pattern for all three of its record types: a record, not a
 * dialog. `draft: true` is explicit on the wire and defaults FALSE server-side —
 * an un-rebuilt client must keep writing reported incidents, because a draft is
 * invisible to everyone but its author and a real fault written as one would
 * silently vanish rather than error. */
export function useCreateSiteIncidentDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<SiteIncidentCreate>) =>
      api.post<SiteIncidentRecord>(BASE, { ...body, draft: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** R-244 — the live form's write path: saves as you leave each field, no Save
 *  button. Absent fields mean LEAVE ALONE, so send only what changed.
 *
 *  Sending either impact or urgency recomputes the priority server-side, which is
 *  why the chip must be read back from the response rather than guessed at. */
export function usePatchSiteIncidentDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Partial<SiteIncidentDraftPatch>) =>
      api.patch<SiteIncidentRecord>(`${BASE}/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** R-244 — "Report it": starts the SLA clock, enters the triage queue, locks the
 *  form. The mandatory fields are demanded HERE rather than at create, in form
 *  order, so the 400 names the first gap. */
export function useReportSiteIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<SiteIncidentRecord>(`${BASE}/${id}/report`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** R-244 — abandoning a draft. A BLANK one is deleted (returns null) so the INC
 *  sequence does not grow a hole for every mis-click; one somebody typed into is
 *  kept as cancelled, because the register does not delete records. */
export function useDiscardSiteIncidentDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<SiteIncidentRecord | null>(`${BASE}/${id}`),
    // ⚠ REMOVE the record's own query before invalidating the rest. A blank draft is
    // DELETED outright, so a plain `invalidateQueries(KEY)` refetches the detail of a
    // row that no longer exists — two 404s in the console on a completely normal
    // path. Removing it first means there is nothing left to refetch; the LIST still
    // invalidates so the discarded draft disappears from it.
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: [...KEY, 'detail', id] });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useTransitionSiteIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
      cause,
      action,
      note,
      resolution_code,
      hold_reason,
      problem_id,
    }: {
      id: string;
      status: Exclude<SiteIncidentStatus, 'new' | 'assigned'>;
      cause?: string;
      action?: string;
      note?: string;
      /** Required when status = resolved (R-140). */
      resolution_code?: SiteIncidentResolutionCode;
      /** Required when status = on_hold (R-140 — SLA pauses). */
      hold_reason?: SiteIncidentHoldReason;
      /** R-143: a linked known-error/problem whose root_cause / workaround
       * prefill cause / action at resolve when left blank. */
      problem_id?: string;
    }) =>
      api.post<SiteIncidentRecord>(`${BASE}/${id}/transition`, {
        status,
        cause,
        action,
        note,
        resolution_code,
        hold_reason,
        problem_id,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** TL one-click: least-loaded member of the incident's group (Approve level). */
export function useAutoAssignSiteIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<SiteIncidentRecord>(`${BASE}/${id}/auto-assign`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAckSiteIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<boolean>(`${BASE}/${id}/ack`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAssignSiteIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, group_id, assignee_id }: { id: string; group_id?: string; assignee_id?: string }) =>
      api.post<SiteIncidentRecord>(`${BASE}/${id}/assign`, { group_id, assignee_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Add an incident comment / comms status-note. Offline-queued (R-145): a note
 *  captured in the field lands in the retry queue on a network failure and
 *  flushes on reconnect. Append-only, so a replay is harmless. */
export function useCommentSiteIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note, kind }: { id: string; note: string; kind?: 'comment' | 'comms' }) =>
      submitQueueable<boolean>({
        method: 'POST',
        path: `${BASE}/${id}/comment`,
        body: { note, kind },
        invalidate: [...KEY],
        label: kind === 'comms' ? 'Incident status-note' : 'Incident comment',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Declare a major incident (Part N; Approve level). MIM defaults to the SOC on-call. */
export function useDeclareMajor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      child_ids,
      mim_user_id,
      bridge_url,
      business_impact,
    }: {
      id: string;
      child_ids?: string[];
      mim_user_id?: string;
      bridge_url?: string;
      /** N-1: business-impact statement, set at declare time. */
      business_impact?: string;
    }) =>
      api.post<SiteIncidentRecord>(`${BASE}/${id}/declare-major`, {
        child_ids,
        mim_user_id,
        bridge_url,
        business_impact,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Bulk-resolve the open children of a major parent (Approve level). */
export function useResolveChildren() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      cause,
      action,
      resolution_code,
      note,
    }: {
      id: string;
      cause: string;
      action: string;
      resolution_code: SiteIncidentResolutionCode;
      note?: string;
    }) =>
      api.post<SiteIncidentRecord[]>(`${BASE}/${id}/resolve-children`, {
        cause,
        action,
        resolution_code,
        note,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// ─── knowledge / problem-links / KB (R-143) ──────────────────────────────────

/** KE-first contextual knowledge search — known-errors first, then KB hits.
 * Debounce at the call site; disabled until there's a query or a class scope. */
export function useKnowledgeSearch(query: KnowledgeQuery, enabled = true) {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.class_code) params.set('class_code', query.class_code);
  const qs = params.toString();
  return useQuery({
    queryKey: [...KEY, 'knowledge', query],
    queryFn: () => api.get<KnowledgeEntry[]>(`/v1/itsm/knowledge${qs ? `?${qs}` : ''}`),
    enabled,
    staleTime: 30_000,
  });
}

/** Link an incident to a problem / known-error (idempotent). Writes a
 * `problem_linked` incident event — invalidate so the detail's events refresh. */
export function useLinkProblem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, problem_id, note }: { id: string; problem_id: string; note?: string }) =>
      api.post<boolean>(`${BASE}/${id}/problem-link`, { problem_id, note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUnlinkProblem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, problem_id }: { id: string; problem_id: string }) =>
      api.delete<boolean>(`${BASE}/${id}/problem-link/${problem_id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Attach a KB article to an incident / rate it helpful|not_helpful. An
 * `attached` action also writes a `kb_attached` incident event. */
export function useAttachKb() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      articleId,
      incidentId,
      action,
    }: {
      articleId: string;
      incidentId: string;
      action?: KbUsageAction;
    }) => api.post<boolean>(`/v1/itsm/kb/${articleId}/attach`, { incident_id: incidentId, action }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Draft a KB article (prefilled server-side from a resolved incident when
 * `source_incident` is given). Mirrors up as `kb_draft_upsert`. */
export function useCreateKbDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: KbDraftCreate) => api.post<KbDraftResult>('/v1/itsm/kb/drafts', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Cloud HQ read of the incident fleet mirror (R-124/R-126; read-only per D-106). */
export function useFleetIncidents(openOnly = true) {
  return useQuery({
    queryKey: [...KEY, 'fleet', { openOnly }],
    queryFn: () =>
      api.get<{ site_id: string; row: SiteIncidentRecord; occurred_at_ms: number }[]>(
        `/users/v1/itsm/fleet/incidents?open_only=${openOnly}`,
      ),
    refetchInterval: 30_000,
  });
}

/** R-238 §6 B2 — how many P1s are open, for the shell's app-wide critical bar.
 *
 *  A dedicated COUNT endpoint rather than counting the list: this rides every page
 *  load in the shell, and the list resolves display names and asset tags per row —
 *  work a number does not need. Same call the unregistered badge makes.
 *
 *  `enabled` is not optional in practice: gate it on the maintenance read permission
 *  AND on a site being selected, or it fires during the cloud console's no-site
 *  window and caches an error under a key that never changes when the site arrives.
 *
 *  A P1 is not a per-second event, but it IS the thing you most want to hear about
 *  late — so this polls harder than the unregistered badge (60s vs 120s) and no
 *  harder than that. */
export function useOpenP1Count(enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'p1-count'],
    queryFn: () => api.get<number>(`${BASE}/p1-count`),
    staleTime: 60_000,
    refetchInterval: 60_000,
    enabled,
  });
}
